import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Star, MessageSquare, CheckCircle2, AlertTriangle, Filter, 
  ThumbsUp, ThumbsDown, Loader2, RefreshCw, X, User, Car
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FeedbackManagement = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [filter, resolvedFilter]);

  const fetchReviews = async () => {
    setLoading(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      let url = `${API_URL}/api/feedback/garage/reviews?limit=100`;
      
      if (filter === 'negative') {
        url += '&max_rating=2';
      } else if (filter === 'positive') {
        url += '&min_rating=4';
      } else if (filter === 'neutral') {
        url += '&min_rating=3&max_rating=3';
      }
      
      if (resolvedFilter === 'resolved') {
        url += '&resolved=true';
      } else if (resolvedFilter === 'unresolved') {
        url += '&resolved=false';
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setReviews(data.reviews || []);
        setStats({
          total: data.total,
          ratingCounts: data.rating_counts,
          negativeCount: data.negative_count,
          unresolvedCount: data.unresolved_count
        });
      }
    } catch (error) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedReview) return;
    
    setResolving(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      const response = await fetch(`${API_URL}/api/feedback/garage/reviews/${selectedReview.id}/resolve`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resolution_notes: resolutionNotes,
          action_taken: actionTaken
        })
      });
      
      if (response.ok) {
        toast.success('Feedback marked as resolved');
        setSelectedReview(null);
        setResolutionNotes('');
        setActionTaken('');
        fetchReviews();
      } else {
        toast.error('Failed to resolve feedback');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setResolving(false);
    }
  };

  const handleUnresolve = async (reviewId) => {
    const token = localStorage.getItem('garage_token');
    
    try {
      const response = await fetch(`${API_URL}/api/feedback/garage/reviews/${reviewId}/unresolve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Feedback marked as unresolved');
        fetchReviews();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-slate-600 text-slate-600'}`}
          />
        ))}
      </div>
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (rating === 3) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="feedback-management">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats?.total || 0}</p>
                <p className={`text-xs ${theme.textSecondary}`}>Total Reviews</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <ThumbsUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>
                  {(stats?.ratingCounts?.['5'] || 0) + (stats?.ratingCounts?.['4'] || 0)}
                </p>
                <p className={`text-xs ${theme.textSecondary}`}>Positive (4-5★)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <ThumbsDown className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats?.negativeCount || 0}</p>
                <p className={`text-xs ${theme.textSecondary}`}>Negative (1-2★)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${theme.cardBg} ${theme.border}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats?.unresolvedCount || 0}</p>
                <p className={`text-xs ${theme.textSecondary}`}>Need Attention</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className={`${theme.cardBg} ${theme.border}`}>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className={`w-4 h-4 ${theme.textSecondary}`} />
              <span className={`text-sm ${theme.textSecondary}`}>Rating:</span>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className={`w-32 ${theme.inputBg} ${theme.border}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="positive">Positive (4-5★)</SelectItem>
                  <SelectItem value="neutral">Neutral (3★)</SelectItem>
                  <SelectItem value="negative">Negative (1-2★)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`text-sm ${theme.textSecondary}`}>Status:</span>
              <Select value={resolvedFilter} onValueChange={setResolvedFilter}>
                <SelectTrigger className={`w-32 ${theme.inputBg} ${theme.border}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unresolved">Unresolved</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchReviews}
              className={`${theme.border} ml-auto`}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reviews List */}
      <Card className={`${theme.cardBg} ${theme.border}`}>
        <CardHeader>
          <CardTitle className={`${theme.textPrimary} flex items-center gap-2`}>
            <MessageSquare className="w-5 h-5 text-orange-400" />
            Customer Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
            </div>
          ) : reviews.length === 0 ? (
            <div className={`text-center py-8 ${theme.textSecondary}`}>
              No feedback found matching your filters
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div 
                  key={review.id}
                  className={`p-4 rounded-lg border ${theme.border} ${theme.pageBg} ${
                    review.rating <= 2 && !review.resolved ? 'border-red-500/30' : ''
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className={`font-medium ${theme.textPrimary}`}>{review.customer_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {renderStars(review.rating)}
                          <Badge className={getRatingColor(review.rating)}>
                            {review.rating}/5
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Car className={`w-3 h-3 ${theme.textSecondary}`} />
                          <span className={`text-xs ${theme.textSecondary}`}>
                            {review.vehicle_info?.make} {review.vehicle_info?.model} • {review.vehicle_info?.number}
                          </span>
                        </div>
                        <p className={`text-xs ${theme.textSecondary} mt-1`}>
                          Job: {review.job_number} • {formatDate(review.submitted_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {review.would_recommend === true && (
                        <Badge className="bg-green-500/20 text-green-400">Would Recommend</Badge>
                      )}
                      {review.would_recommend === false && (
                        <Badge className="bg-red-500/20 text-red-400">Won't Recommend</Badge>
                      )}
                      {review.resolved ? (
                        <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Resolved
                        </Badge>
                      ) : review.rating <= 2 && (
                        <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Needs Attention
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {review.review && (
                    <div className={`mt-3 p-3 rounded ${theme.cardBg} border ${theme.border}`}>
                      <p className={`text-sm ${theme.textPrimary}`}>"{review.review}"</p>
                    </div>
                  )}
                  
                  {/* Detailed ratings */}
                  {(review.service_quality || review.communication || review.value_for_money) && (
                    <div className="flex flex-wrap gap-4 mt-3">
                      {review.service_quality && (
                        <div className={`text-xs ${theme.textSecondary}`}>
                          Service Quality: {renderStars(review.service_quality)}
                        </div>
                      )}
                      {review.communication && (
                        <div className={`text-xs ${theme.textSecondary}`}>
                          Communication: {renderStars(review.communication)}
                        </div>
                      )}
                      {review.value_for_money && (
                        <div className={`text-xs ${theme.textSecondary}`}>
                          Value for Money: {renderStars(review.value_for_money)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Resolution info */}
                  {review.resolved && review.resolution_notes && (
                    <div className={`mt-3 p-3 rounded bg-green-500/10 border border-green-500/20`}>
                      <p className="text-xs text-green-400 font-medium">Resolution Notes:</p>
                      <p className={`text-sm ${theme.textPrimary} mt-1`}>{review.resolution_notes}</p>
                      {review.action_taken && (
                        <>
                          <p className="text-xs text-green-400 font-medium mt-2">Action Taken:</p>
                          <p className={`text-sm ${theme.textPrimary} mt-1`}>{review.action_taken}</p>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    {!review.resolved ? (
                      <Button
                        size="sm"
                        onClick={() => setSelectedReview(review)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Mark Resolved
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnresolve(review.id)}
                        className={`${theme.border}`}
                      >
                        Reopen
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolution Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className={`${theme.cardBgSolid} ${theme.border} w-full max-w-md`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className={theme.textPrimary}>Resolve Feedback</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedReview(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`p-3 rounded ${theme.pageBg} border ${theme.border}`}>
                <p className={`text-sm ${theme.textSecondary}`}>Customer: {selectedReview.customer_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  {renderStars(selectedReview.rating)}
                </div>
                {selectedReview.review && (
                  <p className={`text-sm ${theme.textPrimary} mt-2`}>"{selectedReview.review}"</p>
                )}
              </div>
              
              <div>
                <label className={`text-sm ${theme.textSecondary} mb-1 block`}>Resolution Notes</label>
                <Textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="How was this resolved?"
                  rows={3}
                  className={`${theme.inputBg} ${theme.border}`}
                />
              </div>
              
              <div>
                <label className={`text-sm ${theme.textSecondary} mb-1 block`}>Action Taken</label>
                <Textarea
                  value={actionTaken}
                  onChange={(e) => setActionTaken(e.target.value)}
                  placeholder="What action was taken to address this?"
                  rows={2}
                  className={`${theme.inputBg} ${theme.border}`}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedReview(null)}
                  className={theme.border}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleResolve}
                  disabled={resolving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {resolving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Mark Resolved
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FeedbackManagement;
